import type { PiIconName } from '@uiKit/elements/PiIcon.vue'
import type { AppTab } from '@uiKit/components/Tabs.vue'

// The investor-facing order badge — swagger `displayStatus`. The raw
// `orderStatus` (payment_pending | paid | …) is lifecycle plumbing; every
// badge, note and tracker state on the order surfaces keys on this instead.
export const ORDER_DISPLAY_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  AWAITING_ISSUANCE: 'awaiting_issuance',
  DISTRIBUTING_TOKENS: 'distributing_tokens',
  TOKENS_DISTRIBUTED: 'tokens_distributed',
  COMPLETED: 'completed',
  REJECTED: 'rejected',
  CANCELLED: 'cancelled',
  DELETED: 'deleted',
} as const

export type OrderDisplayStatus = (typeof ORDER_DISPLAY_STATUS)[keyof typeof ORDER_DISPLAY_STATUS]

export type OrderTone = 'warning' | 'info' | 'success' | 'danger'

export interface OrderBadgeConfig {
  label: string
  tone: OrderTone
  icon: PiIconName
}

export const ORDER_BADGE: Record<OrderDisplayStatus, OrderBadgeConfig> = {
  pending: { label: 'Pending', tone: 'warning', icon: 'clock' },
  approved: { label: 'Approved', tone: 'info', icon: 'check-circle' },
  awaiting_issuance: { label: 'Awaiting Issuance', tone: 'warning', icon: 'clock' },
  distributing_tokens: { label: 'Distributing Tokens', tone: 'warning', icon: 'clock' },
  tokens_distributed: { label: 'Tokens Distributed', tone: 'success', icon: 'check-circle' },
  completed: { label: 'Completed', tone: 'success', icon: 'check-circle' },
  rejected: { label: 'Rejected', tone: 'danger', icon: 'times-circle' },
  cancelled: { label: 'Cancelled', tone: 'danger', icon: 'times-circle' },
  deleted: { label: 'Deleted', tone: 'danger', icon: 'times-circle' },
}

const isDisplayStatus = (value: string): value is OrderDisplayStatus => value in ORDER_BADGE

/** Unknown / legacy values fall back to Pending rather than an empty badge. */
export const toDisplayStatus = (value: string | null | undefined): OrderDisplayStatus =>
  value && isDisplayStatus(value) ? value : ORDER_DISPLAY_STATUS.PENDING

// Where the money is for each status — drives the wallet / payment cards.
/** Closed without a debit: the amount was released, nothing left the wallet. */
export const CLOSED_STATUSES: readonly OrderDisplayStatus[] = [
  ORDER_DISPLAY_STATUS.REJECTED,
  ORDER_DISPLAY_STATUS.CANCELLED,
  ORDER_DISPLAY_STATUS.DELETED,
]

/**
 * Rejected / cancelled / deleted — no certificate will ever be issued. An
 * unknown or not-yet-loaded status is NOT closed ("may still come").
 */
export const isClosedStatus = (value: string | null | undefined): boolean =>
  CLOSED_STATUSES.some((status) => status === (value ?? '').toLowerCase())

/** Tokens are out and the payment has settled. */
export const SETTLED_STATUSES: readonly OrderDisplayStatus[] = [
  ORDER_DISPLAY_STATUS.TOKENS_DISTRIBUTED,
  ORDER_DISPLAY_STATUS.COMPLETED,
]

// ── History tabs (Figma 32590:69619) ─────────────────────────────────────
// Four tabs over nine statuses: every in-flight order reads as Pending, every
// order that ended without tokens reads as Rejected.
export interface OrderHistoryTab extends AppTab {
  statuses: OrderDisplayStatus[]
}

export const ORDER_HISTORY_TABS: OrderHistoryTab[] = [
  { key: 'all', label: 'All', statuses: [] },
  {
    key: 'pending',
    label: 'Pending',
    statuses: [
      ORDER_DISPLAY_STATUS.PENDING,
      ORDER_DISPLAY_STATUS.APPROVED,
      ORDER_DISPLAY_STATUS.AWAITING_ISSUANCE,
      ORDER_DISPLAY_STATUS.DISTRIBUTING_TOKENS,
      ORDER_DISPLAY_STATUS.TOKENS_DISTRIBUTED,
    ],
  },
  { key: 'completed', label: 'Completed', statuses: [ORDER_DISPLAY_STATUS.COMPLETED] },
  { key: 'rejected', label: 'Rejected', statuses: [...CLOSED_STATUSES] },
]

export const DEFAULT_ORDER_HISTORY_TAB = 'all'
